'use client';

import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { useTranslation } from 'react-i18next';
import { FaInstagram, FaWhatsapp, FaFacebook } from 'react-icons/fa6';
import { normalizeLanguage, withLanguageParam } from '@/lib/i18nUrl';

export default function Footer() {
  const { t, i18n } = useTranslation('common');
  const language = normalizeLanguage(i18n.resolvedLanguage);
  const homeHref = withLanguageParam('/', language);

  const productLinks = [
    { label: t('nav.benefits'), href: '/#benefits' },
    { label: t('nav.ingredients'), href: '/#ingredients' },
    { label: t('nav.howItWorks'), href: '/#how-it-works' },
    { label: t('nav.trackOrder'), href: '/home/track-order' },
  ];

  const companyLinks = [
    { label: 'About', href: '/about' },
    { label: 'Contact', href: '/contact' },
  ];

  const policyLinks = [
    { label: 'Privacy Policy', href: '/privacy' },
    { label: 'Terms of Service', href: '/terms' },
    // { label: 'Refund & Returns', href: '/refund' },
    { label: 'Shipping Policy', href: '/shipping' },
  ];

  const socials = [
    {
      label: 'Instagram',
      href: 'https://www.instagram.com/dr_detoxer_official',
      icon: <FaInstagram size={18} />,
    },
    {
      label: 'Facebook',
      href: 'https://www.facebook.com/people/Dr-Detoxer/61572030027552/',
      icon: <FaFacebook size={18} />,
    },
    {
      label: 'Whatsapp',
      href: 'https://wa.me/+919054523166?text=Hello%20Dr.%20Detoxer,%0A%0AI%20want%20to%20know%20more%20about%20your%20fruit%20%26%20vegetable%20wash.%20Can%20you%20share%20details%20and%20pricing?',
      icon: <FaWhatsapp size={18} />,
    },
  ];

  const renderLink = (link: { label: string; href: string }) => {
    const isInternal = link.href.startsWith('/');
    const className =
      'text-sm font-medium text-muted hover:text-foreground transition-colors duration-200 min-h-[44px] flex items-center';

    const href = isInternal ? withLanguageParam(link.href, language) : link.href;

    return isInternal ? (
      <Link key={link.label} href={href} className={className}>
        {link.label}
      </Link>
    ) : (
      <a key={link.label} href={href} className={className}>
        {link.label}
      </a>
    );
  };

  return (
    <footer
      className="border-t border-border bg-white py-16 px-6 lg:px-12"
      aria-label="Footer"
    >
      <div className="max-w-7xl mx-auto">
        {/* Top: Logo + tagline + grouped links */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="lg:col-span-4 flex flex-col gap-3">
            <Link
              href={homeHref}
              className="flex items-center gap-3"
              aria-label="Dr. Detoxer home"
            >
              <AppLogo size={32} />
              <span className="font-display font-semibold text-lg text-foreground tracking-tight">
                Dr. Detoxer
              </span>
            </Link>
            <p className="text-muted text-sm font-light max-w-xs">
              {t('footer.tagline')}
            </p>
            <div className="flex items-center gap-4 mt-2">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  className="w-11 h-11 flex items-center justify-center rounded-full border border-border hover:border-primary hover:text-primary transition-all duration-200"
                  aria-label={`Dr. Detoxer on ${s.label}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="text-base">{s.icon}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Product */}
          <div className="lg:col-span-3">
            <h2 className="text-xs font-semibold tracking-widest uppercase text-foreground mb-4">
              Product
            </h2>
            <div className="flex flex-col">{productLinks.map(renderLink)}</div>
          </div>

          {/* Company */}
          <div className="lg:col-span-2">
            <h2 className="text-xs font-semibold tracking-widest uppercase text-foreground mb-4">
              Company
            </h2>
            <div className="flex flex-col">{companyLinks.map(renderLink)}</div>
          </div>

          {/* Policies */}
          <div className="lg:col-span-3">
            <h2 className="text-xs font-semibold tracking-widest uppercase text-foreground mb-4">
              Policies
            </h2>
            <div className="flex flex-col">{policyLinks.map(renderLink)}</div>
          </div>
        </div>

        {/* Bottom row */}
        <div className="mt-10 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-muted text-sm">
            © {new Date().getFullYear()} Dr. Detoxer. {t('footer.rights')}
          </p>
          <p className="text-muted text-xs text-center sm:text-right">
            Made with care in India 🇮🇳 · Website developed by{' '}
            <a
              href="https://www.spiritsolutions.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground underline-offset-2 hover:underline"
            >
              Spirit Solutions
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
